import { palette, updatePreset } from "@primevue/themes";
import { computed, watch } from "vue";
import { ref } from "vue";
import { DEFAULT_LISA_THEME, normalizeLISATheme, type LisaThemeType } from "./LISATheme";
import { isLISA3 } from "@/composables/useLISA3";

export const lisaThemeLocal = ref<LisaThemeType>(normalizeLISATheme(DEFAULT_LISA_THEME));
export type { LisaThemeType } from "./LISATheme";

const lisaThemePrimeVue4 = ref({
  primitive: {
    borderRadius: { none: "0", xs: "2px", sm: "4px", md: "4px", lg: "4px", xl: "6px" },
    blue: {
      //primary
      50: "#eff6ff",
      100: "#dbeafe",
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb",
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
      950: "#172554",
    },
    slate: {
      //secondary
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
      950: "#020617",
    },
    green: {
      //success
      50: "#f0fdf4",
      100: "#dcfce7",
      200: "#bbf7d0",
      300: "#86efac",
      400: "#4ade80",
      500: "#22c55e",
      600: "#16a34a",
      700: "#15803d",
      800: "#166534",
      900: "#14532d",
      950: "#052e16",
    },
    red: {
      //danger
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      300: "#fca5a5",
      400: "#f87171",
      500: "#ef4444",
      600: "#dc2626",
      700: "#b91c1c",
      800: "#991b1b",
      900: "#7f1d1d",
      950: "#450a0a",
    },
    sky: {
      //info
      50: "#f0f9ff",
      100: "#e0f2fe",
      200: "#bae6fd",
      300: "#7dd3fc",
      400: "#38bdf8",
      500: "#0ea5e9",
      600: "#0284c7",
      700: "#0369a1",
      800: "#075985",
      900: "#0c4a6e",
      950: "#082f49",
    },
    orange: {
      //warn
      50: "#fff7ed",
      100: "#ffedd5",
      200: "#fed7aa",
      300: "#fdba74",
      400: "#fb923c",
      500: "#f97316",
      600: "#ea580c",
      700: "#c2410c",
      800: "#9a3412",
      900: "#7c2d12",
      950: "#431407",
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            secondary: {
              background: "#f1f5f9",
              hoverBackground: "#e2e8f0",
              activeBackground: "#cbd5e1",
              borderColor: "#f1f5f9",
              hoverBorderColor: "#e2e8f0",
              activeBorderColor: "#cbd5e1",
              color: "#475569",
              hoverColor: "#334155",
              activeColor: "#1e293b",
              focusRing: {
                color: "#475569",
                shadow: "none",
              },
            },
          },
          outlined: {
            secondary: {
              hoverBackground: "#f8fafc",
              activeBackground: "#f1f5f9",
              borderColor: "#e2e8f0",
              color: "#64748b",
            },
          },
          text: {
            secondary: {
              hoverBackground: "#f8fafc",
              activeBackground: "#f1f5f9",
              color: "#64748b",
            },
          },
        },
      },
    },
  },
});

function changeLisaTheme(theme: LisaThemeType) {
  const colorMapping = [
    ["primary-color", "blue"],
    ["success-color", "green"],
    ["warning-color", "orange"],
    ["danger-color", "red"],
    ["info-color", "sky"],
  ] as const;

  colorMapping.forEach(([color, baseColor]) => {
    const colors = palette(theme[color]);
    lisaThemePrimeVue4.value.primitive[baseColor] =
      colors as typeof lisaThemePrimeVue4.value.primitive.blue;
  });

  const secondaryColors = palette(
    theme["secondary-color"],
  ) as typeof lisaThemePrimeVue4.value.primitive.slate;
  const secondaryButton = lisaThemePrimeVue4.value.components.button.colorScheme.light;

  secondaryButton.root.secondary = {
    background: secondaryColors[100],
    hoverBackground: secondaryColors[200],
    activeBackground: secondaryColors[300],
    borderColor: secondaryColors[100],
    hoverBorderColor: secondaryColors[200],
    activeBorderColor: secondaryColors[300],
    color: secondaryColors[600],
    hoverColor: secondaryColors[700],
    activeColor: secondaryColors[800],
    focusRing: {
      color: secondaryColors[600],
      shadow: "none",
    },
  };
  secondaryButton.outlined.secondary = {
    hoverBackground: secondaryColors[50],
    activeBackground: secondaryColors[100],
    borderColor: secondaryColors[200],
    color: secondaryColors[500],
  };
  secondaryButton.text.secondary = {
    hoverBackground: secondaryColors[50],
    activeBackground: secondaryColors[100],
    color: secondaryColors[500],
  };

  lisaThemePrimeVue4.value.primitive.borderRadius.sm = theme["border-radius"];
  lisaThemePrimeVue4.value.primitive.borderRadius.md = theme["border-radius"];
  lisaThemePrimeVue4.value.primitive.borderRadius.lg = theme["border-radius"];
  lisaThemePrimeVue4.value.primitive.borderRadius.xl = theme["border-radius"];

  document.documentElement.style.setProperty("--p-lisa-valid", theme["lisa-valid"]);
  document.documentElement.style.setProperty("--p-lisa-invalid", theme["lisa-invalid"]);
  document.documentElement.style.setProperty("--p-lisa-neutral", theme["lisa-neutral"]);

  if (isLISA3.value === true) {
    document.documentElement.style.setProperty(
      "--lisa-input-height",
      "calc(var(--lisaui-spacing) * 10)",
    );

    document.documentElement.style.setProperty("--lisa-primary", theme["primary-color"]);
    document.documentElement.style.removeProperty("--lisa-primary-foreground");

    document.documentElement.style.setProperty("--lisa-secondary", theme["secondary-color"]);
    document.documentElement.style.removeProperty("--lisa-secondary-foreground");

    document.documentElement.style.setProperty("--lisa-success", theme["success-color"]);
    document.documentElement.style.removeProperty("--lisa-success-foreground");

    document.documentElement.style.setProperty("--lisa-warning", theme["warning-color"]);
    document.documentElement.style.removeProperty("--lisa-warning-foreground");

    document.documentElement.style.setProperty("--lisa-info", theme["info-color"]);
    document.documentElement.style.removeProperty("--lisa-info-foreground");

    document.documentElement.style.setProperty("--lisa-error", theme["danger-color"]);
    document.documentElement.style.removeProperty("--lisa-error-foreground");

    document.documentElement.style.setProperty("--lisa-contrast-bias", "25%");

    // test lisa ui lib
    document.documentElement.style.setProperty("--lisa-radius-sm", `${theme["border-radius"]}`);
    document.documentElement.style.setProperty("--lisa-radius-md", `${theme["border-radius"]}`);
    document.documentElement.style.setProperty("--lisa-radius-lg", `${theme["border-radius"]}`);

    return;
  }

  document.documentElement.style.setProperty("--lisa-primary", theme["primary-color"]);
  document.documentElement.style.setProperty(
    "--lisa-primary-foreground",
    "var(--p-primary-contrast-color)",
  );

  document.documentElement.style.setProperty("--lisa-secondary", theme["secondary-color"]);
  document.documentElement.style.setProperty(
    "--lisa-secondary-foreground",
    "var(--p-button-secondary-color)",
  );

  document.documentElement.style.setProperty("--lisa-success", theme["success-color"]);
  document.documentElement.style.setProperty(
    "--lisa-success-foreground",
    "var(--p-button-success-color)",
  );

  document.documentElement.style.setProperty("--lisa-warning", theme["warning-color"]);
  document.documentElement.style.setProperty(
    "--lisa-warning-foreground",
    "var(--p-button-warn-color)",
  );

  document.documentElement.style.setProperty("--lisa-info", theme["info-color"]);
  document.documentElement.style.setProperty(
    "--lisa-info-foreground",
    "var(--p-button-info-color)",
  );

  document.documentElement.style.setProperty("--lisa-error", theme["danger-color"]);
  document.documentElement.style.setProperty(
    "--lisa-error-foreground",
    "var(--p-button-danger-color)",
  );
}

export function useLISAThemeProviderHelper() {
  const lisaTheme = computed({
    get() {
      return lisaThemeLocal.value;
    },
    set(theme: LisaThemeType) {
      lisaThemeLocal.value = normalizeLISATheme(theme);
    },
  });
  watch(
    [lisaThemeLocal, isLISA3],
    ([theme]) => {
      changeLisaTheme(theme);
      updatePreset(lisaThemePrimeVue4.value);
    },
    { deep: true },
  );
  return {
    lisaTheme,
    setDefaultTheme: () => {
      lisaThemeLocal.value = normalizeLISATheme(DEFAULT_LISA_THEME);
    },
    loadCurrentTheme: () => {
      changeLisaTheme(lisaThemeLocal.value);
      updatePreset(lisaThemePrimeVue4.value);
    },
  };
}
